import React, { useEffect, useMemo, useState } from 'react';
import { ModalShell } from '../ds/ModalShell';
import { InputField } from '../ds/InputField';
import { Button } from '../ds/components/Button';
import { Icon } from '../ds/components/Icon';
import { apartmentsApi } from '../services/api';
import {
  DISPLAY_LABEL_MAX,
  STAIRCASE_MAX,
  apartmentDoorMarking,
  planLabelRange,
} from '../utils/apartmentLabels';
import { lokaleLabel } from '../utils/plural';

export interface ApartmentLabelsModalProps {
  /** Lokale budynku; `null` = okno zamknięte. */
  apartments: { id: string; apartmentNumber: string; displayLabel?: string | null; staircase?: string | null }[] | null;
  buildingId: string;
  /** Lokal, od którego zaczynamy (ołówek w wierszu) — jego pole „Drzwi" dostaje fokus. */
  focusApartmentId?: string | null;
  /** Adres budynku do tytułu. */
  subject?: string;
  onClose: () => void;
  /** Zapis się udał — rodzic odświeża budynek (po zamknięciu okna). */
  onSaved: () => void;
}

interface Draft { displayLabel: string; staircase: string }

const banner = (kind: 'error' | 'ok' | 'info'): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 'var(--space-2)',
  padding: 'var(--space-2) var(--space-3)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-label)',
  background: kind === 'error' ? 'var(--status-deny-bg)' : kind === 'ok' ? 'var(--status-ok-bg)' : 'var(--surface-fill)',
  border: `var(--border-width) solid ${kind === 'error' ? 'var(--status-deny-bar)' : kind === 'ok' ? 'var(--status-ok-bar)' : 'var(--border)'}`,
  color: kind === 'error' ? 'var(--status-deny)' : kind === 'ok' ? 'var(--status-ok)' : 'var(--text-secondary)',
});

const td: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-3)',
  borderBottom: 'var(--border-width) solid var(--border)',
  color: 'var(--text-primary)',
  verticalAlign: 'middle',
};

const cellInput = (invalid: boolean): React.CSSProperties => ({
  width: '100%',
  minWidth: 72,
  minHeight: 40,
  padding: 'var(--space-1) var(--space-2)',
  fontSize: 'var(--text-body)',
  fontFamily: 'var(--font-sans)',
  color: 'var(--text-primary)',
  background: 'var(--surface-card)',
  border: `var(--border-width) solid ${invalid ? 'var(--status-deny-bar)' : 'var(--border-strong)'}`,
  borderRadius: 'var(--radius-md)',
  boxSizing: 'border-box',
});

const toDraft = (a: { displayLabel?: string | null; staircase?: string | null }): Draft => ({
  displayLabel: a.displayLabel ?? '',
  staircase: a.staircase ?? '',
});

const byNumber = (a: { apartmentNumber: string }, b: { apartmentNumber: string }) =>
  String(a.apartmentNumber).localeCompare(String(b.apartmentNumber), 'pl', { numeric: true });

const parseIntOrNull = (v: string): number | null => (v.trim() === '' ? null : Number(v.trim()));

/**
 * „Oznaczenia lokali" (E3, raport uwag Bronka 2026-10-19; Wolności 532 — numeracja krocząca po klatkach): klatka
 * i numer na drzwiach obok numeru z CEEB. Pojedynczo (pola w wierszu) i hurtowo („Klatka od–do": lokale 13–24 →
 * kl. II, drzwi 1–12; podgląd w tabeli przed zapisem). Numer lokalu się NIE zmienia — to tożsamość w CEEB; bot,
 * import i załącznik CEEB etykiet nie czytają. Zapis: PUT /api/apartments/labels (tylko zmienione lokale).
 */
export default function ApartmentLabelsModal({
  apartments, buildingId, focusApartmentId, subject, onClose, onSaved,
}: ApartmentLabelsModalProps) {
  const open = !!apartments && apartments.length > 0;
  const sorted = useMemo(() => (open ? [...apartments!].sort(byNumber) : []), [open, apartments]);
  // Klucz zamiast tablicy: rodzic podaje nowy literał co render, a stan resetujemy tylko przy otwarciu/zmianie lokali.
  const key = open ? `${apartments!.map((a) => `${a.id}:${a.displayLabel ?? ''}:${a.staircase ?? ''}`).join(',')}|${focusApartmentId ?? ''}` : '';

  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [range, setRange] = useState({ from: '', to: '', staircase: '', doorFrom: '' });
  const [rangeInfo, setRangeInfo] = useState<{ kind: 'error' | 'info'; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDrafts(Object.fromEntries(apartments!.map((a) => [a.id, toDraft(a)])));
    setRange({ from: '', to: '', staircase: '', doorFrom: '' });
    setRangeInfo(null);
    setSaveError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const changed = sorted.filter((a) => {
    const d = drafts[a.id];
    if (!d) return false;
    const was = toDraft(a);
    return d.displayLabel.trim() !== was.displayLabel.trim() || d.staircase.trim() !== was.staircase.trim();
  });
  const rowError = (d?: Draft): string | null => {
    if (!d) return null;
    if (d.displayLabel.trim().length > DISPLAY_LABEL_MAX) return `Drzwi: najwyżej ${DISPLAY_LABEL_MAX} znaków`;
    if (d.staircase.trim().length > STAIRCASE_MAX) return `Klatka: najwyżej ${STAIRCASE_MAX} znaków`;
    return null;
  };
  const hasErrors = changed.some((a) => rowError(drafts[a.id]));

  const setField = (id: string, field: keyof Draft, value: string) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...(prev[id] || { displayLabel: '', staircase: '' }), [field]: value } }));
    setSaveError(null);
  };

  const applyRange = () => {
    const plan = planLabelRange(sorted, {
      from: Number(range.from.trim() || NaN),
      to: Number(range.to.trim() || NaN),
      staircase: range.staircase,
      doorFrom: parseIntOrNull(range.doorFrom),
    });
    if ('error' in plan) { setRangeInfo({ kind: 'error', text: plan.error }); return; }
    if (plan.rows.length === 0) {
      setRangeInfo({ kind: 'error', text: `W budynku nie ma lokali o numerach ${range.from.trim()}–${range.to.trim()}.` });
      return;
    }
    setDrafts((prev) => {
      const next = { ...prev };
      for (const r of plan.rows) {
        const cur = next[r.apartmentId] || { displayLabel: '', staircase: '' };
        next[r.apartmentId] = {
          displayLabel: r.displayLabel ?? cur.displayLabel,
          staircase: r.staircase ?? cur.staircase,
        };
      }
      return next;
    });
    setRangeInfo({
      kind: 'info',
      text: `Wypełniono ${lokaleLabel(plan.rows.length)} — sprawdź w tabeli i zapisz.`
        + (plan.skipped.length ? ` Pominięte (nietypowy numer — uzupełnij w wierszu): ${plan.skipped.join(', ')}.` : ''),
    });
  };

  const submit = async () => {
    if (!open || changed.length === 0 || hasErrors) return;
    setSaving(true);
    setSaveError(null);
    try {
      await apartmentsApi.updateLabels(buildingId, changed.map((a) => {
        const d = drafts[a.id];
        return { apartmentId: a.id, displayLabel: d.displayLabel.trim() || null, staircase: d.staircase.trim() || null };
      }));
      onSaved();
      onClose();
    } catch (e) {
      console.error('Zapis oznaczeń lokali nieudany:', e);
      setSaveError(e instanceof Error && e.message ? e.message : 'Nie udało się zapisać oznaczeń. Sprawdź połączenie z serwerem.');
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <>
      <Button variant="secondary" onClick={onClose} disabled={saving}>Anuluj</Button>
      <Button variant="primary" icon="check" onClick={submit} disabled={saving || changed.length === 0 || hasErrors}>
        {saving ? 'Zapisywanie…' : `Zapisz oznaczenia (${changed.length})`}
      </Button>
    </>
  );

  return (
    <ModalShell
      open={open}
      title={subject ? `Oznaczenia lokali — ${subject}` : 'Oznaczenia lokali'}
      width={720}
      onClose={onClose}
      footer={footer}
    >
      <div data-testid="apartment-labels" style={{ display: 'grid', gap: 'var(--space-4)', fontFamily: 'var(--font-sans)' }}>
        <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
          Numer lokalu to numer z CEEB — zostaje bez zmian (bot i import dopasowują po nim lokale). Klatka i numer na
          drzwiach są tylko do wyświetlania: na tablecie, w widoku budynku i na protokole lokalu („Oznaczenie na drzwiach").
        </p>

        <section aria-label="Klatka od–do" style={{ display: 'grid', gap: 'var(--space-2)' }}>
          <strong style={{ fontSize: 'var(--text-body)', color: 'var(--text-heading)' }}>Klatka od–do</strong>
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <InputField label="Lokale od" inputMode="numeric" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} containerStyle={{ width: 110 }} fullWidth={false} />
            <InputField label="do" inputMode="numeric" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} containerStyle={{ width: 110 }} fullWidth={false} />
            <InputField label="Klatka" value={range.staircase} maxLength={STAIRCASE_MAX} onChange={(e) => setRange((r) => ({ ...r, staircase: e.target.value }))} containerStyle={{ width: 110 }} fullWidth={false} />
            <InputField label="Drzwi od" inputMode="numeric" value={range.doorFrom} onChange={(e) => setRange((r) => ({ ...r, doorFrom: e.target.value }))} containerStyle={{ width: 110 }} fullWidth={false} />
            <Button variant="secondary" icon="check" onClick={applyRange}>Wypełnij</Button>
          </div>
          <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
            Np. lokale 13–24 → klatka II, drzwi od 1: lokal 13 = drzwi 1, lokal 24 = drzwi 12. Puste pole „Klatka" albo
            „Drzwi od" zostawia tę wartość bez zmian.
          </span>
          {rangeInfo && (
            <div role={rangeInfo.kind === 'error' ? 'alert' : 'status'} style={banner(rangeInfo.kind)}>
              <Icon name={rangeInfo.kind === 'error' ? 'triangle-alert' : 'check'} size={18} />
              <span>{rangeInfo.text}</span>
            </div>
          )}
        </section>

        {saveError && (
          <div role="alert" style={banner('error')}>
            <Icon name="triangle-alert" size={18} />
            <span>{saveError}</span>
          </div>
        )}

        <div style={{ overflow: 'auto', maxHeight: '45vh' }}>
          <table aria-label="Oznaczenia lokali" style={{ borderCollapse: 'collapse', width: '100%', fontSize: 'var(--text-label)' }}>
            <thead>
              <tr style={{ background: 'var(--surface-fill)', textAlign: 'left' }}>
                <th style={td}>Lokal (CEEB)</th>
                <th style={td}>Klatka</th>
                <th style={td}>Drzwi</th>
                <th style={td}>Widok</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((a) => {
                const d = drafts[a.id] || toDraft(a);
                const err = rowError(d);
                const isChanged = changed.includes(a);
                const marking = apartmentDoorMarking(d);
                return (
                  <tr key={a.id} data-testid={`labels-row-${a.id}`} style={isChanged ? { background: 'var(--surface-hover)' } : undefined}>
                    <td style={{ ...td, fontWeight: 'var(--weight-semibold)' as any, whiteSpace: 'nowrap' }}>Lokal {a.apartmentNumber}</td>
                    <td style={td}>
                      <input
                        aria-label={`Klatka lokalu ${a.apartmentNumber}`}
                        value={d.staircase}
                        maxLength={STAIRCASE_MAX}
                        onChange={(e) => setField(a.id, 'staircase', e.target.value)}
                        style={cellInput(!!err)}
                      />
                    </td>
                    <td style={td}>
                      <input
                        aria-label={`Drzwi lokalu ${a.apartmentNumber}`}
                        value={d.displayLabel}
                        maxLength={DISPLAY_LABEL_MAX}
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus={a.id === focusApartmentId}
                        onChange={(e) => setField(a.id, 'displayLabel', e.target.value)}
                        style={cellInput(!!err)}
                      />
                    </td>
                    <td style={{ ...td, color: err ? 'var(--status-deny)' : 'var(--text-secondary)' }}>
                      {err || (marking ? `${a.apartmentNumber} (${marking})` : '—')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </ModalShell>
  );
}
